import { renderElement } from "../../utils/use-render.js";
import { useMenuOptionState } from "../menu/context.js";
import { useAudioTrackOptions } from "../audio-track/use-audio-track-options.js";
import { MenuRadioGroup } from "../menu/radio-group.js";
import { AudioTrackRadioGroupDataAttrs } from "@videojs/core";
import { getStateDataAttrs } from "@videojs/core/dom";
import { Fragment, createContext, forwardRef, useContext } from "react";
import { jsx } from "react/jsx-runtime";
import { isFunction } from "@videojs/utils/predicate";
//#region src/ui/audio-track-radio-group/component.tsx
const AudioTrackRadioGroupContext = createContext(void 0);
/** Owns audio-track option state and shares it with an enclosing menu. Does not render a DOM element. */
function AudioTrackRadioGroupRoot({ children, ...props }) {
	const audioTrack = useAudioTrackOptions(props);
	useMenuOptionState(toMenuOptionState(audioTrack));
	return /* @__PURE__ */ jsx(AudioTrackRadioGroupContext.Provider, {
		value: audioTrack,
		children
	});
}
/** Displays the selected audio-track label. */
const AudioTrackRadioGroupValue = forwardRef(function AudioTrackRadioGroupValue({ render, className, style, ...elementProps }, forwardedRef) {
	const audioTrack = useAudioTrackRadioGroupContext();
	if (!audioTrack) return null;
	return renderElement("span", {
		render,
		className,
		style
	}, {
		state: audioTrack,
		ref: forwardedRef,
		props: [elementProps, { children: audioTrack.selectedLabel }]
	});
});
/** Renders menu radio items for the player's available audio tracks. */
const AudioTrackRadioGroupOptions = forwardRef(function AudioTrackRadioGroupOptions(componentProps, forwardedRef) {
	const { renderItem, render, className, style, "aria-label": ariaLabelProp, "aria-labelledby": ariaLabelledBy, ...elementProps } = componentProps;
	const audioTrack = useAudioTrackRadioGroupContext();
	if (!audioTrack) return null;
	const { state, value, options, setValue } = audioTrack;
	const ariaLabel = ariaLabelProp ?? (ariaLabelledBy === void 0 ? audioTrack.label : void 0);
	return /* @__PURE__ */ jsx(MenuRadioGroup, {
		...getStateDataAttrs(state, AudioTrackRadioGroupDataAttrs),
		...elementProps,
		ref: forwardedRef,
		className: isFunction(className) ? className(state) : className,
		style: isFunction(style) ? style(state) : style,
		render: isFunction(render) ? (props) => render(props, state) : render,
		value,
		onValueChange: setValue,
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		"aria-disabled": state.disabled || void 0,
		hidden: state.hidden,
		children: options.map((option) => {
			const itemState = {
				...option,
				checked: option.value === value
			};
			return /* @__PURE__ */ jsx(Fragment, { children: renderItem({
				value: option.value,
				disabled: option.disabled,
				"data-track": option.value,
				children: option.label
			}, itemState) }, option.value);
		})
	});
});
/** @internal Compatibility adapter for the existing preset sources. */
const AudioTrackRadioGroupLegacy = forwardRef(function AudioTrackRadioGroupLegacy({ label, formatTrack, disabled, ...props }, forwardedRef) {
	return /* @__PURE__ */ jsx(AudioTrackRadioGroupRoot, {
		label,
		formatTrack,
		disabled,
		children: /* @__PURE__ */ jsx(AudioTrackRadioGroupOptions, {
			...props,
			ref: forwardedRef
		})
	});
});
function useAudioTrackRadioGroupContext() {
	const audioTrack = useContext(AudioTrackRadioGroupContext);
	if (audioTrack === void 0) throw new Error("AudioTrackRadioGroup parts must be used within AudioTrackRadioGroup.Root");
	return audioTrack;
}
function toMenuOptionState(audioTrack) {
	return {
		value: audioTrack?.selectedLabel ?? "",
		disabled: audioTrack?.disabled ?? true,
		hidden: audioTrack?.hidden ?? true,
		availability: audioTrack?.state.availability ?? "unsupported"
	};
}
//#endregion
export { AudioTrackRadioGroupLegacy, AudioTrackRadioGroupOptions, AudioTrackRadioGroupRoot, AudioTrackRadioGroupValue };

//# sourceMappingURL=component.js.map